// 결과 한 장 카드의 내용. 완료한 기록에서만 고르고, 기록이 없는 칸은 비워 둔다(만들어 채우지 않는다).
// 제목 문구는 서사 축 데이터의 막별 titlePhrase, 숫자·전환점은 실제 기록이다. 선정 기준은 게임 규칙이다.
import { GROUPS, GROUP_LABEL } from './rules.js';
import { summarize } from './summary.js';
import { OUTCOME_LABEL } from './events.js';
import { jobTitle } from './engine.js';

const STEM_KO = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const STEM_ELEMENT = ['목', '목', '화', '화', '토', '토', '금', '금', '수', '수'];
export const GYEOK_LIMIT = '격국은 월지 본기 기준의 단순 판정입니다.';

// 제목: 마지막 직함과 산 햇수. 직업이 없으면 햇수만 쓴다. (일간 이름은 제목이 아니라 부제목으로 쓴다)
function titleOf(run, endAge) {
  return run.job ? `${jobTitle(run.job)}의 ${endAge}년` : `${endAge}년의 기록`;
}

/** 부제목용: 원국의 일간(예: 신금 일간) */
function dayLabelOf(run) {
  const stem = run.saju?.day?.stem;
  return Number.isInteger(stem) ? `${STEM_KO[stem]}${STEM_ELEMENT[stem]} 일간` : '';
}

function extremeEvent(events, fmt, sign) {
  let best = null;
  for (const entry of events) {
    for (const [field, delta] of Object.entries(entry.effects ?? {})) {
      if (!GROUPS.includes(field) || !Number.isFinite(delta) || delta * sign <= 0) continue;
      if (!best || delta * sign > best.delta * sign) best = { entry, field, delta };
    }
  }
  if (!best) return null;
  // 선택 문구만으로는 성공한 일처럼 읽히므로, 결과가 실패였다면 그렇게 밝힌다.
  const failed = best.entry.outcome === 'failure' ? ` (${OUTCOME_LABEL.failure ?? '실패'})` : '';
  return { kind: sign > 0 ? 'gain' : 'loss', age: best.entry.age,
    title: `${best.entry.choiceLabel ? `${best.entry.title} · ${best.entry.choiceLabel}` : best.entry.title}${failed}`,
    detail: `${GROUP_LABEL[best.field]} ${fmt(best.delta)}` };
}

function biggestTransition(run, sum) {
  const moves = Array.isArray(run.jobHistory) ? run.jobHistory : [];
  if (moves.length) {
    const last = moves.reduce((a, b) => (b.age >= a.age ? b : a));
    return { kind: 'transition', age: last.age, title: `${last.from?.name ?? '이전'} 일을 그만두고 ${last.to?.name ?? '새'} 일을 시작했다`, detail: '전직' };
  }
  const promotions = sum.jobs.filter((entry) => String(entry.text).startsWith('승진'));
  if (promotions.length) {
    const last = promotions.reduce((a, b) => (b.age >= a.age ? b : a));
    return { kind: 'transition', age: last.age, title: last.text, detail: '마지막 승진' };
  }
  const first = sum.jobs[0];
  return first ? { kind: 'transition', age: first.age, title: first.text, detail: '첫 직업' } : null;
}

/** { title, numbers: [{group,label,value}×3], turningPoints: [gain|null, loss|null, transition|null], limits } */
export function buildResultCard(run) {
  const sum = summarize(run);
  const ranked = GROUPS.map((group, order) => ({ group, order, value: Number.isFinite(run.stats?.[group]) ? run.stats[group] : 0 }))
    .sort((a, b) => b.value - a.value || a.order - b.order).slice(0, 3);
  return {
    title: titleOf(run, sum.endAge),
    dayLabel: dayLabelOf(run),
    numbers: ranked.map(({ group, value }) => ({ group, label: GROUP_LABEL[group], value: Math.round(value) })),
    turningPoints: [extremeEvent(sum.events, sum.fmt, 1), extremeEvent(sum.events, sum.fmt, -1), biggestTransition(run, sum)],
    limits: [GYEOK_LIMIT],
    endAge: sum.endAge,
  };
}
